import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { PetHeaderSticker } from "./types";
import cls from "./styles.module.scss";
import { plantDetails } from "./plantDetails";

export function PlantArtwork({
  sticker,
  canvasWidth,
  canvasHeight,
  ghost,
  interactive,
}: {
  sticker: PetHeaderSticker;
  canvasWidth: number;
  canvasHeight: number;
  ghost: boolean;
  interactive: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    top: 0,
    placement: "below" as "above" | "below",
  });
  const [now, setNow] = useState(Date.now);
  const plant = sticker.plant!;
  const stage = plant.currentStage;
  const ratio = stage.imageWidth / sticker.asset.imageWidth;
  const description = plantDetails(
    plant,
    (key, options) => t(key, options ?? {}),
    now
  );
  const artworkWidth = sticker.width * canvasWidth;
  const artworkHeight =
    (artworkWidth * sticker.asset.imageHeight) / sticker.asset.imageWidth;
  const tooltipAbove = tooltipPosition.placement === "above";
  const tooltip = (
    <span
      className={`${cls.plantTooltip} ${
        tooltipAbove ? cls.plantTooltipAbove : cls.plantTooltipBelow
      }`}
      role="status"
      style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
    >
      {description}
    </span>
  );

  const toggleTooltip = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (open) {
      setOpen(false);
      return;
    }

    const artworkRect = event.currentTarget.getBoundingClientRect();
    const spaceAbove = artworkRect.top;
    const spaceBelow = window.innerHeight - artworkRect.bottom;
    const placement =
      spaceBelow < 96 && spaceAbove > spaceBelow ? "above" : "below";
    const tooltipWidth = Math.min(210, window.innerWidth * 0.7);
    const halfTooltip = tooltipWidth / 2;
    const center = artworkRect.left + artworkRect.width / 2;
    const left = Math.min(
      Math.max(center, halfTooltip + 8),
      window.innerWidth - halfTooltip - 8
    );

    setTooltipPosition({
      left: left + window.scrollX,
      top:
        (placement === "above" ? artworkRect.top : artworkRect.bottom) +
        window.scrollY,
      placement,
    });
    setOpen(true);
  };

  return (
    <button
      type="button"
      className={cls.plantArtwork}
      data-plant-id={sticker.id}
      data-plant-stage={plant.stage}
      aria-label={`${sticker.asset.name}. ${description}`}
      title={description}
      aria-expanded={interactive ? open : undefined}
      tabIndex={interactive ? 0 : -1}
      onClick={toggleTooltip}
      onMouseEnter={() => setNow(Date.now())}
      onFocus={() => setNow(Date.now())}
      onBlur={() => setOpen(false)}
      style={{
        left: sticker.x * canvasWidth,
        top: sticker.y * canvasHeight,
        width: artworkWidth,
        height: artworkHeight,
        zIndex: sticker.stackOrder,
        pointerEvents: interactive ? "auto" : "none",
      }}
    >
      {ghost && (
        <img
          className={cls.plantGhost}
          data-plant-ghost
          src={sticker.asset.imageUrl}
          alt=""
          draggable={false}
        />
      )}
      <img
        className={cls.plantStage}
        src={stage.imageUrl}
        alt=""
        draggable={false}
        style={{
          width: `${ratio * 100}%`,
          left: `${(plant.finalAnchorX - ratio * stage.anchorX) * 100}%`,
        }}
      />
      {open && interactive && createPortal(tooltip, document.body)}
    </button>
  );
}
